import styled, { css } from 'styled-components'
import { Button, Pill, Tonality } from '@dsny/dsny-component-library'
import {
  RELEASE_INFORMATION_LAYOUT_QUERIES,
  releaseInfoCoverControlsDownQuery,
  releaseInfoLargeDesktopUpQuery,
  releaseInfoMobileDownQuery,
  releaseInfoReactGridLargeUpQuery,
  releaseInfoSmallDesktopQuery,
  releaseInfoSmallDesktopUpQuery,
  releaseInfoTabletDownQuery,
  releaseInfoTabletUpQuery
} from './responsiveQueries'

export const SIDE_PANEL_WIDTH_DEFAULT = 568
export const SIDE_PANEL_WIDTH_RESPONSIVE = 320
export const SIDE_PANEL_WIDTH_2K = 860

export const ReleaseInfoFluidContainer = styled.div`
  box-sizing: border-box;
  position: relative;
  width: 100%;
  margin-right: auto;
  margin-left: auto;
  padding-right: 15px;
  padding-left: 15px;
`

export const LegacyContentColumn = styled.div`
  box-sizing: border-box;
  position: relative;
  width: 100%;
  min-height: 1px;
  max-width: 100%;
  padding-right: 12px;
  padding-left: 12px;
  flex: 0 0 100%;
  transition: flex-basis 0.3s ease;

  @media ${releaseInfoReactGridLargeUpQuery} {
    flex-basis: ${({ $fullWidth }) => ($fullWidth ? '100%' : '50%')};
    max-width: ${({ $fullWidth }) => ($fullWidth ? '100%' : '50%')};
  }
`

export const LegacyActionsDropdownColumn = styled.div`
  box-sizing: border-box;
  position: relative;
  width: 100%;
  min-height: 1px;
  max-width: 100%;
  padding-right: 15px;
  padding-left: 15px;
  flex: 0 0 100%;

  @media ${releaseInfoReactGridLargeUpQuery} {
    flex-basis: 16.666667%;
    max-width: 16.666667%;
  }
`

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;

  &[data-layout='column'] {
    height: auto;
    overflow: visible;
  }

  &:not([data-layout='column']) {
    height: 100%;
    overflow: hidden;

    @media ${releaseInfoTabletDownQuery} {
      overflow-y: auto;
      overflow-x: hidden;
    }
  }
`

export const Header = styled.div`
  position: relative;
  flex-shrink: 0;
  transition: margin-right 0.3s ease;

  &.shifted {
    margin-right: ${SIDE_PANEL_WIDTH_DEFAULT}px;

    @media ${releaseInfoSmallDesktopQuery} {
      margin-right: ${SIDE_PANEL_WIDTH_RESPONSIVE}px;
    }
    @media ${RELEASE_INFORMATION_LAYOUT_QUERIES.sidePanelUltraWideUp} {
      margin-right: ${SIDE_PANEL_WIDTH_2K}px;
    }

    @media ${releaseInfoTabletDownQuery} {
      margin-right: 0;
    }
  }
`

export const ScrollableContent = styled.div`
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: margin-right 0.3s ease;

  &.shifted {
    margin-right: ${SIDE_PANEL_WIDTH_DEFAULT}px;

    @media ${releaseInfoSmallDesktopQuery} {
      margin-right: ${SIDE_PANEL_WIDTH_RESPONSIVE}px;
    }
    @media ${RELEASE_INFORMATION_LAYOUT_QUERIES.sidePanelUltraWideUp} {
      margin-right: ${SIDE_PANEL_WIDTH_2K}px;
    }

    @media ${releaseInfoTabletDownQuery} {
      margin-right: 0;
    }
  }

  @media ${releaseInfoTabletDownQuery} {
    overflow: visible;
  }

  .desktop-fixed-height {
    @media ${releaseInfoTabletDownQuery} {
      height: auto !important;
      flex: none !important;
    }
  }

  .legacy-tabs-section {
    @media ${releaseInfoSmallDesktopUpQuery} {
      display: flex;
      flex-direction: column;
      height: 100%;
      min-height: 0;
    }
  }
`

export const StackedScrollableContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding: 24px;
  padding-bottom: 90px;

  @media ${releaseInfoTabletDownQuery} {
    padding: 16px;
    padding-bottom: 90px;
  }
`

export const StackedSection = styled.div`
  width: 100%;
  height: 100%;
`

export const LegacyBackgroundLayer = styled.div`
  width: 100%;
  height: 100%;
  background-repeat: no-repeat;
  background-size: cover;
  position: absolute;

  &:after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background-color: ${({ theme }) => theme.colors.neutralW10};
    pointer-events: none;
    opacity: 0.9;
  }
`

export const BackgroundLayer = styled.div`
  width: 100%;
  height: 100%;
  max-height: 308px;
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  border-bottom: 1px solid #393939;

  &:after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background-color: ${({ theme }) => theme.colors.neutralW10};
    pointer-events: none;
    opacity: 0.9;
  }

  @media ${releaseInfoMobileDownQuery} {
    max-height: 100%;
  }
`

export const ReleaseInfoContainer = styled.div`
  width: 100%;
  display: flex;
  margin-left: 16px;
  @media ${releaseInfoMobileDownQuery} {
    flex-direction: column;
    display: block;
    margin-left: 0;
  }
`

export const ReleaseInformationLevelCoverArtCol = styled.div`
  flex: 0 0 180px;
  width: 180px;
  min-width: 180px;
  max-width: 180px;
  height: auto;
  align-self: flex-start;
  @media ${releaseInfoMobileDownQuery} {
    flex: 0 0 100%;
    width: 100%;
    min-width: 100%;
    max-width: 100%;
    margin-bottom: 24px;
    padding: 0 16px !important;
  }

  &:hover {
    button {
      display: flex;
    }
  }
`

const coverArtExpandVisibility = css`
  &:hover button,
  &:focus-within button {
    display: flex;
  }

  @media ${releaseInfoCoverControlsDownQuery} {
    button {
      display: flex;
    }
  }
`

export const CoverArtContainer = styled.div`
  position: relative;
  width: 100%;
  ${coverArtExpandVisibility}

  @media ${releaseInfoMobileDownQuery} {
    max-width: none;
    flex: 1 1 auto;
  }

  @media ${releaseInfoTabletUpQuery} {
    width: 180px;
  }
`

export const LegacyCoverArtContainer = styled(CoverArtContainer)``

export const ExpandButton = styled(Button)`
  display: none;
  width: 44px;
  height: 44px;
  padding: 0;
  position: absolute;
  top: 12px;
  left: 12px;
  border-radius: 10px;
  border: none;
  background: ${({ theme }) => theme.colors.interact20};
  box-shadow: 0px 8px 20px 0px rgba(0, 0, 0, 0.28);
  align-items: center;
  justify-content: center;

  &:hover {
    cursor: pointer;
    background: ${({ theme }) => theme.colors.interact30};
  }

  @media ${releaseInfoCoverControlsDownQuery} {
    display: flex;
  }
`

export const CanadianPillWrapper = styled.div`
  padding-top: 10px;
  position: absolute;
  left: 10px;
  border-radius: 5px;
`

export const OptionsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  padding: 0;
  position: relative;
  bottom: 70px;
  border-radius: 5px;
  align-items: center;
`
export const PlayIconWrapper = styled.div`
  display: flex;
  position: absolute;
  right: 10px;
  width: 40px;
  height: 40px;
  align-items: center;
  justify-content: center;
  flex-direction: row;
  border-radius: 50%;
  cursor: pointer;
  background-color: ${({ theme }) => theme.colors.neutralW100};

  &:hover {
    background-color: ${({ theme }) => theme.colors.neutralW80};
  }
`

export const CoverArt = styled.img`
  margin-bottom: 24px;
  aspect-ratio: 1/1;
  object-fit: cover;
  border-radius: 5px;
  display: block;
  width: 180px;
`

export const LegacyCoverArt = styled(CoverArt)`
  margin-bottom: 24px;
  width: 100%;
  aspect-ratio: 1/1;
  object-fit: cover;
  border-radius: 5px;

  @media ${releaseInfoLargeDesktopUpQuery} {
    max-width: 270px;
    max-height: 270px;
  }

  @media ${RELEASE_INFORMATION_LAYOUT_QUERIES.coverArtMediumUp} {
    max-width: 300px;
    max-height: 300px;
  }

  @media ${RELEASE_INFORMATION_LAYOUT_QUERIES.coverArtLargeUp} {
    max-width: 400px;
    max-height: 400px;
  }
`

export const Empty = styled.div`
  display: flex;
  padding: 56px 0px;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.neutralW70};
  padding-bottom: 290px;
`

export const CanadianContentPill = styled(Pill)`
  width: ${({ $isMobile }) => ($isMobile ? '102px' : '128px')};
  margin-bottom: ${({ $isMobile }) => ($isMobile ? 0 : '12px')};
  background-color: ${({ theme }) => theme.colors.neutralW30};
  color: ${({ theme }) => theme.colors.neutralW90};
`

export const ErrorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
`

export const StyledButton = styled(Tonality.Button)`
  display: flex;
  width: 100%;
  white-space: nowrap;

  @media ${releaseInfoMobileDownQuery} {
    margin-bottom: 15px;
  }

  @media ${releaseInfoTabletUpQuery} {
    display: inline-flex;
  }

  @media ${releaseInfoSmallDesktopUpQuery} {
    max-width: 180px;
  }

  @media ${releaseInfoLargeDesktopUpQuery} {
    max-width: 180px;
    width: 180px;
    margin-right: 8px;
  }
`
